// src/components/Warehouses/ITB/ITBWarehouseDetail.js
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Package,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  Archive,
  Search,
  Filter,
  Download,
  RefreshCw,
  TrendingDown,
  Box,
  Loader
} from 'lucide-react';

const ITBWarehouseDetail = () => {
  const { warehouseId } = useParams();
  const [inventory, setInventory] = useState([]);
  const [warehouseInfo, setWarehouseInfo] = useState(null);
  const [stats, setStats] = useState({
    total_items: 0,
    total_skus: 0,
    low_stock_items: 0,
    out_of_stock_items: 0,
    negative_atp_items: 0,
    quarantined_items: 0,
    total_on_hand: 0,
    total_available: 0,
    total_quarantined: 0
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('sku');
  const [sortOrder, setSortOrder] = useState('asc');

  // Fetch ITB inventory data
  const fetchInventory = async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      setError('');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}api/dashboard/warehouse/${warehouseId}/inventory`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch ITB inventory data');
      }

      const data = await response.json();
      console.log('📦 ITB inventory data:', data);

      if (data && data.success) {
        setWarehouseInfo(data.warehouse);

        // Filter items with stock
        const inventoryItems = data.inventory.filter(item =>
          item.quantity_on_hand > 0 || item.total_available_to_promise > 0
        );
        setInventory(inventoryItems);
        setStats(data.stats || {});

        console.log(`✅ Loaded ${inventoryItems.length} ITB items`);
      } else {
        throw new Error(data?.error?.message || 'Failed to load ITB inventory');
      }

    } catch (error) {
      console.error('❌ Error fetching ITB inventory:', error);
      setError('Failed to load ITB inventory: ' + error.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Filter and sort inventory
  const getFilteredAndSortedInventory = () => {
    let filtered = [...inventory];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.sku.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    switch (filterStatus) {
      case 'in_stock':
        filtered = filtered.filter(item => item.quantity_on_hand > 0 && !item.is_low_stock);
        break;
      case 'low_stock':
        filtered = filtered.filter(item => item.is_low_stock);
        break;
      case 'out_of_stock':
        filtered = filtered.filter(item => item.is_out_of_stock);
        break;
      case 'negative_atp':
        filtered = filtered.filter(item => item.has_negative_atp);
        break;
      case 'quarantined':
        filtered = filtered.filter(item => item.quantity_in_quarantine > 0);
        break;
      default:
        // 'all' - no additional filter
        break;
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let compareValue = 0;

      switch (sortBy) {
        case 'sku':
          compareValue = a.sku.localeCompare(b.sku);
          break;
        case 'on_hand':
          compareValue = a.quantity_on_hand - b.quantity_on_hand;
          break;
        case 'available':
          compareValue = a.total_available_to_promise - b.total_available_to_promise;
          break;
        case 'reserved':
          compareValue = a.reserved_stock - b.reserved_stock;
          break;
        default:
          compareValue = 0;
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

    return filtered;
  };

  const filteredInventory = useMemo(
    () => getFilteredAndSortedInventory(),
    [inventory, searchTerm, filterStatus, sortBy, sortOrder]
  );

  // Get stock status badge
  const getStockStatusBadge = (item) => {
    if (item.has_negative_atp) {
      return { color: '#ef4444', bg: '#fee2e2', text: 'Negative ATP', icon: AlertTriangle };
    }
    if (item.is_out_of_stock) {
      return { color: '#ef4444', bg: '#fee2e2', text: 'Out of Stock', icon: AlertCircle };
    }
    if (item.is_low_stock) {
      return { color: '#f59e0b', bg: '#fef3c7', text: 'Low Stock', icon: TrendingDown };
    }
    if (item.quantity_in_quarantine > 0) {
      return { color: '#6b7280', bg: '#f3f4f6', text: 'Has Quarantine', icon: Archive };
    }
    return { color: '#10b981', bg: '#d1fae5', text: 'In Stock', icon: CheckCircle };
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['SKU', 'Facility', 'On Hand', 'Available', 'Reserved', 'Quarantined', 'Status'];
    const rows = filteredInventory.map(item => [
      item.sku,
      item.facility,
      item.quantity_on_hand,
      item.total_available_to_promise,
      item.reserved_stock,
      item.quantity_in_quarantine,
      getStockStatusBadge(item).text
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `itb_inventory_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  useEffect(() => {
    fetchInventory();
    const interval = setInterval(() => fetchInventory(true), 5 * 60 * 1000); // Auto-refresh every 5 minutes
    return () => clearInterval(interval);
  }, [warehouseId]);

  // Loading state
  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-main">
          <div style={{textAlign: 'center', padding: '2rem'}}>
            <Loader className="loading-spinner" style={{margin: '0 auto'}} />
            <p style={{marginTop: '1rem', color: '#6b7280'}}>
              Loading ITB inventory data...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-main">
          <div className="error-message">
            <AlertTriangle size={24} />
            <div>
              <h3>Error Loading ITB Inventory</h3>
              <p className="error-text">{error}</p>
              <button
                onClick={() => fetchInventory()}
                className="btn-primary"
                style={{marginTop: '1rem'}}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-main">
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h1>ITB Warehouse Inventory</h1>
            <p style={{color: '#6b7280', marginTop: '0.25rem'}}>
              {warehouseInfo?.location} • Deposco Integration
            </p>
          </div>
          <div style={{display: 'flex', gap: '0.5rem'}}>
            <button
              onClick={exportToCSV}
              className="btn-secondary"
              disabled={filteredInventory.length === 0}
            >
              <Download size={16} />
              Export CSV
            </button>
            <button
              onClick={() => fetchInventory(true)}
              className="btn-primary"
              disabled={refreshing}
            >
              {refreshing ? (
                <>
                  <Loader size={16} className="spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw size={16} />
                  Refresh
                </>
              )}
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div className="stat-card" style={{backgroundColor: '#f0f9ff', borderLeft: '4px solid #3b82f6'}}>
            <div className="stat-label">Total SKUs</div>
            <div className="stat-value" style={{color: '#3b82f6'}}>
              {stats.total_skus}
            </div>
          </div>

          <div className="stat-card" style={{backgroundColor: '#f0fdf4', borderLeft: '4px solid #10b981'}}>
            <div className="stat-label">Total On Hand</div>
            <div className="stat-value" style={{color: '#10b981'}}>
              {stats.total_on_hand?.toLocaleString()}
            </div>
          </div>

          <div className="stat-card" style={{backgroundColor: '#fef3c7', borderLeft: '4px solid #f59e0b'}}>
            <div className="stat-label">Total Available</div>
            <div className="stat-value" style={{color: '#f59e0b'}}>
              {stats.total_available?.toLocaleString()}
            </div>
          </div>

          <div className="stat-card" style={{backgroundColor: '#fee2e2', borderLeft: '4px solid #ef4444'}}>
            <div className="stat-label">Low Stock</div>
            <div className="stat-value" style={{color: '#ef4444'}}>
              {stats.low_stock_items}
            </div>
          </div>

          <div className="stat-card" style={{backgroundColor: '#f3f4f6', borderLeft: '4px solid #6b7280'}}>
            <div className="stat-label">Quarantined</div>
            <div className="stat-value" style={{color: '#6b7280'}}>
              {stats.quarantined_items}
            </div>
          </div>

          <div className="stat-card" style={{backgroundColor: '#fce7f3', borderLeft: '4px solid #ec4899'}}>
            <div className="stat-label">Negative ATP</div>
            <div className="stat_value" style={{color: '#ec4899'}}>
              {stats.negative_atp_items}
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="filter-container">
          <div className="search-box" style={{flex: 1}}>
            <Search size={20} />
            <input
              type="text"
              placeholder="Search by SKU..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Items</option>
            <option value="in_stock">In Stock</option>
            <option value="low_stock">Low Stock</option>
            <option value="out_of_stock">Out of Stock</option>
            <option value="negative_atp">Negative ATP</option>
            <option value="quarantined">Has Quarantine</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="filter-select"
          >
            <option value="sku">Sort by SKU</option>
            <option value="on_hand">Sort by On Hand</option>
            <option value="available">Sort by Available</option>
            <option value="reserved">Sort by Reserved</option>
          </select>

          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="btn-icon"
            title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>

        {/* Results count */}
        <p style={{color: '#6b7280', marginBottom: '1rem'}}>
          Showing {filteredInventory.length} of {inventory.length} items
        </p>

        {/* Inventory Table */}
        <div style={{backgroundColor: 'white', borderRadius: '0.5rem', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse'}}>
              <thead style={{backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb'}}>
                <tr>
                  <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>SKU</th>
                  <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Facility</th>
                  <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>On Hand</th>
                  <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Available</th>
                  <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Reserved</th>
                  <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Quarantined</th>
                  <th style={{padding: '0.75rem', textAlign: 'left', fontWeight: '600'}}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredInventory.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{padding: '2rem', textAlign: 'center', color: '#6b7280'}}>
                      {searchTerm || filterStatus !== 'all'
                        ? 'No items match your filters'
                        : 'No inventory items found'}
                    </td>
                  </tr>
                ) : (
                  filteredInventory.map((item) => {
                    const statusBadge = getStockStatusBadge(item);
                    const StatusIcon = statusBadge.icon;

                    return (
                      <tr key={item.sku} style={{borderBottom: '1px solid #f3f4f6'}}>
                        <td style={{padding: '0.75rem'}}>
                          <div style={{fontWeight: '500', color: '#1f2937', fontFamily: 'monospace'}}>
                            {item.sku}
                          </div>
                        </td>

                        <td style={{padding: '0.75rem'}}>
                          <span style={{color: '#6b7280', fontSize: '0.875rem'}}>
                            {item.facility}
                          </span>
                        </td>

                        <td style={{padding: '0.75rem'}}>
                          <span style={{
                            fontWeight: '600',
                            color: item.quantity_on_hand > 0 ? '#1f2937' : '#ef4444',
                            fontSize: '1rem'
                          }}>
                            {item.quantity_on_hand.toLocaleString()}
                          </span>
                        </td>

                        <td style={{padding: '0.75rem'}}>
                          <span style={{
                            fontWeight: '500',
                            color: item.total_available_to_promise < 0 ? '#ef4444' : '#374151'
                          }}>
                            {item.total_available_to_promise.toLocaleString()}
                          </span>
                        </td>

                        <td style={{padding: '0.75rem'}}>
                          <span style={{
                            color: item.reserved_stock > 0 ? '#f59e0b' : '#9ca3af'
                          }}>
                            {item.reserved_stock.toLocaleString()}
                          </span>
                        </td>

                        <td style={{padding: '0.75rem'}}>
                          <span style={{
                            color: item.quantity_in_quarantine > 0 ? '#ef4444' : '#9ca3af'
                          }}>
                            {item.quantity_in_quarantine}
                          </span>
                        </td>

                        <td style={{padding: '0.75rem'}}>
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '9999px',
                            backgroundColor: statusBadge.bg,
                            color: statusBadge.color,
                            fontSize: '0.75rem',
                            fontWeight: '500'
                          }}>
                            <StatusIcon size={14} />
                            {statusBadge.text}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ITBWarehouseDetail;